import { ReactNode, useCallback, useEffect } from "react";
import {
  TextFraction,
  TextState,
} from "./Fractions/Text/TextEditor/TextFraction";
import {
  ImageFraction,
  ImageFractionState,
} from "./Fractions/Image/ImageFraction";
import { LinkFraction, LinkFractionState } from "./Fractions/Link/LinkFraction";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Divider,
  VStack,
} from "@chakra-ui/react";
import {
  HeadingFraction,
  HeadingFractionState,
} from "./Fractions/Heading/HeadingFraction";
import { v4 as uuidv4 } from "uuid";
import { getUnixTimestamp } from "../../../Helpers/time";
import { FractionState, FractionType } from "./Fractions/Fraction";
import { Toolbar } from "./Toolbar";
import { AliasType } from "../PostFlow";
import {
  QueryFraction,
  QueryFractionState,
} from "./Fractions/Query/QueryFraction";

// Fraction data persisted in database
export interface FractionPersistantData {
  type: FractionType;
  state: FractionState;
  id: string;
  position: number;
}

// Post data persisted in database
// A post consists of fractions
export interface PostPersistantData {
  fractions: FractionPersistantData[];
  id: string | undefined;
  hidden: boolean;
  timing: TimingState | undefined;
  lastEditedTime: number | undefined;
  createTime: number;
  pinTime: number | undefined | null; // The time when it was pinned, or undefined if not pinned
  foldname: string | undefined;
}

export function createNewPostFraction(
  type: FractionType,
  position: number
): FractionPersistantData {
  return {
    type: type,
    state: undefined,
    id: generateTempId(),
    position: position,
  };
}

export enum DisplayMode {
  DISPLAY,
  DISPLAY_EDITABLE,
  EDIT,
}

export interface PostProps {
  state: PostPersistantData;
  onStateUpdate: (state: PostPersistantData) => void;
  mode: DisplayMode;
  fractionInFocus: string | undefined;
  setFractionInFocus: (id: string | undefined) => void;
  onPin: (pin: boolean) => void;
  onHide: (hidden: boolean) => void;
  onEdit: () => void;
  onSetTiming: (timing: TimingState | undefined) => void;
  onCreateNewTiming: () => void;
  onCopy: () => void;
  onDelete: () => void;
  alias: AliasType;
}

function generateTempId() {
  return uuidv4();
}

export function Post(props: PostProps) {
  const getFractionMode = useCallback(
    (id: string) => {
      if (props.mode === DisplayMode.EDIT) {
        return id === props.fractionInFocus
          ? DisplayMode.EDIT
          : DisplayMode.DISPLAY_EDITABLE;
      }
      return DisplayMode.DISPLAY;
    },
    [props.fractionInFocus, props.mode]
  );

  const setFractions = (fractions: FractionPersistantData[]) => {
    const newState: PostPersistantData = props.state
      ? {
          ...props.state,
          fractions: fractions,
        }
      : {
          fractions: fractions,
          createTime: getUnixTimestamp(),
          hidden: false,
          lastEditedTime: undefined,
          pinTime: undefined,
          timing: undefined,
          foldname: undefined,
          id: "",
        };
    props.onStateUpdate(newState);
  };

  useEffect(() => {
    if (props.state.fractions.length === 0) {
      setFractions([
        {
          type: "heading",
          state: { text: "", center: false, size: "xl" },
          id: generateTempId(),
          position: -1, // Always on top
        },
      ]);
    }
  }, []);

  const configOnStateChange = (id: string) => {
    return (newState: FractionState) =>
      setFractions(
        props.state.fractions.map((f) =>
          f.id === id ? { ...f, state: newState } : f
        )
      );
  };

  const configOnModeChange = (id: string) => {
    return (newMode: DisplayMode) => {
      if (newMode === DisplayMode.EDIT) {
        props.setFractionInFocus(id);
      } else if (id === props.fractionInFocus) {
        props.setFractionInFocus(undefined);
      }
    };
  };

  const configOnMoveDown = (id: string) => {
    return () => {
      const mvDownFragment = props.state.fractions.find((f) => f.id === id);
      const upperPosition = mvDownFragment?.position;

      const mvUpFragment = props.state.fractions.find(
        (f) => f.position - 1 === upperPosition
      );
      const lowerPosition = mvUpFragment?.position;

      if (mvDownFragment !== undefined && mvUpFragment !== undefined) {
        setFractions(
          props.state.fractions.map((f) =>
            f.id === mvDownFragment?.id
              ? { ...f, position: lowerPosition! }
              : f.id === mvUpFragment.id
              ? { ...f, position: upperPosition! }
              : f
          )
        );
      }
    };
  };

  const configOnMoveUp = (id: string) => {
    return () => {
      const mvUpFragment = props.state.fractions.find((f) => f.id === id);
      const lowerPosition = mvUpFragment?.position;

      const mvDownFragment = props.state.fractions.find(
        (f) => f.position + 1 === lowerPosition
      );
      const upperPosition = mvDownFragment?.position;

      if (mvDownFragment !== undefined && mvUpFragment !== undefined) {
        setFractions(
          props.state.fractions.map((f) =>
            f.id === mvDownFragment.id
              ? { ...f, position: lowerPosition! }
              : f.id === mvUpFragment.id
              ? { ...f, position: upperPosition! }
              : f
          )
        );
      }
    };
  };

  const configOnDelete = (id: string) => {
    return () => {
      setFractions(
        // Remove fraction with given ID (except if it has a position < 0, i.e. the heading fragment)
        props.state.fractions.filter((f) => f.id !== id || f.position < 0)
      );
    };
  };

  return (
    <div style={{ marginTop: "10px", marginBottom: "10px" }}>
      <Fold foldname={props.state.foldname}>
        <Box
          backgroundColor={shouldHidePost(props.state) ? "gray.100" : undefined} // Change the color as needed
          opacity={shouldHidePost(props.state) ? 0.3 : 1} // Adjust the opacity as needed
          zIndex={1000} // Adjust the zIndex to make sure it covers everything
        >
          <VStack spacing={"15px"} alignItems={"left"} marginTop={"20px"}>
            {props.state.fractions
              .sort((a, b) => a.position - b.position)
              .map((fraction, index) =>
                fraction.type === "heading" ? (
                  <HeadingFraction
                    key={fraction.id}
                    mode={getFractionMode(fraction.id)}
                    state={fraction.state as HeadingFractionState}
                    onStateChange={configOnStateChange(fraction.id)}
                    onModeChange={configOnModeChange(fraction.id)}
                    moveDown={configOnMoveDown(fraction.id)}
                    moveUp={configOnMoveUp(fraction.id)}
                    onDelete={configOnDelete(fraction.id)}
                    alias={props.alias}
                  />
                ) : fraction.type === "text" ? (
                  <TextFraction
                    key={fraction.id}
                    mode={getFractionMode(fraction.id)}
                    state={fraction.state as TextState}
                    onStateChange={configOnStateChange(fraction.id)}
                    onModeChange={configOnModeChange(fraction.id)}
                    moveDown={configOnMoveDown(fraction.id)}
                    moveUp={configOnMoveUp(fraction.id)}
                    onDelete={configOnDelete(fraction.id)}
                    alias={props.alias}
                  />
                ) : fraction.type === "image" ? (
                  <ImageFraction
                    key={fraction.id}
                    mode={getFractionMode(fraction.id)}
                    state={fraction.state as ImageFractionState}
                    onStateChange={configOnStateChange(fraction.id)}
                    onModeChange={configOnModeChange(fraction.id)}
                    moveDown={configOnMoveDown(fraction.id)}
                    moveUp={configOnMoveUp(fraction.id)}
                    onDelete={configOnDelete(fraction.id)}
                  />
                ) : fraction.type === "links" ? (
                  <LinkFraction
                    key={fraction.id}
                    mode={getFractionMode(fraction.id)}
                    state={fraction.state as LinkFractionState}
                    onStateChange={configOnStateChange(fraction.id)}
                    onModeChange={configOnModeChange(fraction.id)}
                    moveDown={configOnMoveDown(fraction.id)}
                    moveUp={configOnMoveUp(fraction.id)}
                    onDelete={configOnDelete(fraction.id)}
                  />
                ) : fraction.type === "query" ? (
                  <QueryFraction
                    key={fraction.id}
                    mode={getFractionMode(fraction.id)}
                    state={fraction.state as QueryFractionState}
                    onStateChange={configOnStateChange(fraction.id)}
                    onModeChange={configOnModeChange(fraction.id)}
                    moveDown={configOnMoveDown(fraction.id)}
                    moveUp={configOnMoveUp(fraction.id)}
                    onDelete={configOnDelete(fraction.id)}
                  />
                ) : undefined
              )}

            <Divider />
            {/*<hr style={{ border: "1px solid gray.100" }} />*/}
          </VStack>
        </Box>
      </Fold>

      {props.mode === DisplayMode.DISPLAY_EDITABLE && (
        <Toolbar
          pin={{
            state: props.state.pinTime != null,
            onStateChange: props.onPin,
          }}
          hide={{ state: props.state.hidden, onStateChange: props.onHide }}
          timing={{
            state: props.state.timing,
            onStateChange: props.onSetTiming,
            onCreationRequest: props.onCreateNewTiming,
          }}
          edit={{ onAction: props.onEdit }}
          copy={{ onAction: props.onCopy }}
          delete={{ onAction: props.onDelete }}
        />
      )}
    </div>
  );
}

export interface TimingState {
  showStartEpoch: Date | undefined;
  showEndEpoch: Date | undefined;
}

export function shouldHidePost(state: PostPersistantData) {
  if (state.hidden) {
    return true;
  }

  const now = getUnixTimestamp();
  const start = state.timing?.showStartEpoch?.getTime();
  const end = state.timing?.showEndEpoch?.getTime() ?? now;
  const inVisibleRange = start === undefined || (start < now && now <= end);
  return !inVisibleRange;
}

export interface FoldProps {
  foldname: string | undefined;
  children: ReactNode;
}

const Fold: React.FC<FoldProps> = ({ foldname, children }: FoldProps) => {
  if (foldname === undefined || foldname.trim() === "") {
    return <>{children}</>;
  }

  return (
    <Accordion
      allowMultiple
      background="blue.500"
      borderRadius={"md"}
      shadow={"2xl"}
    >
      <AccordionItem>
        <AccordionButton>
          <Box textColor={"white"} flex="1" textAlign="left">
            <b>{foldname}</b>
          </Box>
          <AccordionIcon color={"white"} />
        </AccordionButton>
        <AccordionPanel
          background={"gray.50"}
          pb={4}
          borderWidth={"4px"}
          borderColor="blue.500"
        >
          {children}
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};
